<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLeadIntentsStore } from 'dashboard/stores/leadIntents';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const { t } = useI18n();
const store = useLeadIntentsStore();
const pipelinesStore = usePipelinesStore();

const dialogRef = ref(null);

const blankForm = () => ({
  name: '',
  description: '',
  pipeline_id: '',
  active: true,
});

const form = ref(blankForm());
const editingId = ref(null);
const showForm = ref(false);

const pipelineOptions = computed(() => [
  { value: '', label: t('LEAD_INTENTS.FORM.PIPELINE_NONE') },
  ...pipelinesStore.list.map(p => ({ value: p.id, label: p.name })),
]);

const pipelineName = id => pipelinesStore.list.find(p => p.id === id)?.name;

const canSave = computed(() => form.value.name.trim().length > 0);

const open = async () => {
  dialogRef.value?.open();
  resetForm();
  await Promise.all([
    store.fetchAll(),
    pipelinesStore.list.length ? Promise.resolve() : pipelinesStore.fetchAll(),
  ]);
};

const close = () => dialogRef.value?.close();

function resetForm() {
  form.value = blankForm();
  editingId.value = null;
  showForm.value = false;
}

function startNew() {
  form.value = blankForm();
  editingId.value = null;
  showForm.value = true;
}

function startEdit(intent) {
  form.value = {
    name: intent.name,
    description: intent.description || '',
    pipeline_id: intent.pipeline_id || '',
    active: intent.active !== false,
  };
  editingId.value = intent.id;
  showForm.value = true;
}

async function save() {
  if (!canSave.value || store.saving) return;
  const payload = {
    name: form.value.name.trim(),
    description: form.value.description.trim(),
    pipeline_id: form.value.pipeline_id || null,
    active: form.value.active,
  };
  try {
    if (editingId.value) {
      await store.update(editingId.value, payload);
      useAlert(t('LEAD_INTENTS.UPDATED'));
    } else {
      await store.create(payload);
      useAlert(t('LEAD_INTENTS.CREATED'));
    }
    resetForm();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('LEAD_INTENTS.ERROR'));
  }
}

async function remove(intent) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LEAD_INTENTS.DELETE_CONFIRM', { name: intent.name }))) {
    return;
  }
  try {
    await store.destroy(intent.id);
    useAlert(t('LEAD_INTENTS.DELETED'));
    if (editingId.value === intent.id) resetForm();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('LEAD_INTENTS.ERROR'));
  }
}

onMounted(() => {
  if (!pipelinesStore.list.length) pipelinesStore.fetchAll();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="mb-1 text-heading-3 text-n-slate-12">
          {{ t('LEAD_INTENTS.TITLE') }}
        </h3>
        <p class="text-body-small text-n-slate-11">
          {{ t('LEAD_INTENTS.DESCRIPTION') }}
        </p>
      </div>

      <!-- Formulário (novo/editar) -->
      <div
        v-if="showForm"
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-1"
      >
        <Input
          id="intent-name"
          v-model="form.name"
          :label="t('LEAD_INTENTS.FORM.NAME')"
          :placeholder="t('LEAD_INTENTS.FORM.NAME_PLACEHOLDER')"
        />
        <Input
          id="intent-description"
          v-model="form.description"
          :label="t('LEAD_INTENTS.FORM.DESCRIPTION')"
          :placeholder="t('LEAD_INTENTS.FORM.DESCRIPTION_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">
            {{ t('LEAD_INTENTS.FORM.PIPELINE') }}
          </label>
          <Select v-model="form.pipeline_id" :options="pipelineOptions" />
          <p class="text-label-small text-n-slate-10">
            {{ t('LEAD_INTENTS.FORM.PIPELINE_HELP') }}
          </p>
        </div>
        <label class="flex gap-2 items-center cursor-pointer">
          <Checkbox v-model="form.active" />
          <span class="text-body-small text-n-slate-12">
            {{ t('LEAD_INTENTS.FORM.ACTIVE') }}
          </span>
        </label>
        <div class="flex gap-2 justify-end pt-1">
          <NextButton
            faded
            slate
            size="sm"
            :label="t('LEAD_INTENTS.CANCEL')"
            @click="resetForm"
          />
          <NextButton
            size="sm"
            :label="t('LEAD_INTENTS.SAVE')"
            :is-loading="store.saving"
            :disabled="!canSave"
            @click="save"
          />
        </div>
      </div>

      <!-- Botão novo -->
      <NextButton
        v-else
        faded
        slate
        size="sm"
        icon="i-lucide-plus"
        class="self-start"
        :label="t('LEAD_INTENTS.NEW')"
        @click="startNew"
      />

      <!-- Lista -->
      <div
        v-if="store.loading"
        class="flex justify-center py-8 text-n-slate-10"
      >
        <span class="i-lucide-loader-circle size-5 animate-spin" />
      </div>
      <div
        v-else-if="!store.list.length"
        class="flex flex-col gap-1 items-center px-4 py-8 text-center rounded-xl border border-dashed border-n-weak"
      >
        <span class="mb-1 i-lucide-target size-6 text-n-slate-9" />
        <span class="text-body-small text-n-slate-10">
          {{ t('LEAD_INTENTS.EMPTY') }}
        </span>
      </div>
      <ul v-else class="flex flex-col gap-1.5 m-0 list-none max-h-72 overflow-y-auto">
        <li
          v-for="intent in store.list"
          :key="intent.id"
          class="flex gap-3 items-center px-3 py-2.5 rounded-xl border border-n-weak"
        >
          <span
            class="flex justify-center items-center rounded-lg size-8 shrink-0 bg-n-iris-3 text-n-iris-11"
          >
            <span class="i-lucide-target size-4" />
          </span>
          <div class="flex flex-col flex-1 min-w-0">
            <div class="flex gap-2 items-center">
              <span class="text-body-main font-medium truncate text-n-slate-12">
                {{ intent.name }}
              </span>
              <span
                v-if="intent.active === false"
                class="px-1.5 py-px rounded text-label-small font-medium uppercase bg-n-alpha-2 text-n-slate-11"
              >
                {{ t('LEAD_INTENTS.INACTIVE') }}
              </span>
            </div>
            <span class="text-label-small text-n-slate-10 truncate">
              <template v-if="pipelineName(intent.pipeline_id)">
                {{ t('LEAD_INTENTS.MAPS_TO') }}: {{ pipelineName(intent.pipeline_id) }}
              </template>
              <template v-else>{{ t('LEAD_INTENTS.NO_PIPELINE') }}</template>
            </span>
          </div>
          <NextButton
            faded
            slate
            size="sm"
            icon="i-lucide-pen-square"
            @click="startEdit(intent)"
          />
          <NextButton
            faded
            ruby
            size="sm"
            icon="i-lucide-trash-2"
            @click="remove(intent)"
          />
        </li>
      </ul>

      <div class="flex justify-end pt-2 border-t border-n-weak">
        <NextButton
          faded
          slate
          :label="t('LEAD_INTENTS.CLOSE')"
          @click="close"
        />
      </div>
    </div>
  </Dialog>
</template>
